import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Fab, 
  Zoom, 
  Fade,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import N8nChat from './N8nChat';

interface FloatingChatWidgetProps {
  webhookUrl: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  theme?: any;
  layout?: any;
  behavior?: any;
}

// Session storage keys
const STORAGE_KEYS = {
  SESSION_ID: 'n8n_chat_session_id',
  USER_NAME: 'n8n_chat_user_name',
  USER_EMAIL: 'n8n_chat_user_email',
  TERMS_ACCEPTED: 'n8n_chat_terms_accepted',
  CHAT_HISTORY: 'n8n_chat_history',
  SESSION_TIMESTAMP: 'n8n_chat_session_timestamp'
};

// Session expiration time (24 hours)
const SESSION_EXPIRY_HOURS = 24;

// Helper functions for localStorage
const storage = {
  get: (key: string) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
      return null;
    }
  },
  
  set: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn('Failed to write to localStorage:', error);
    }
  },
  
  remove: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to remove from localStorage:', error);
    }
  },
  
  clear: () => {
    try {
      Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }
};

// Check if session is expired
const isSessionExpired = (timestamp: string): boolean => {
  const sessionTime = new Date(timestamp).getTime();
  const currentTime = new Date().getTime();
  const hoursDiff = (currentTime - sessionTime) / (1000 * 60 * 60);
  return hoursDiff > SESSION_EXPIRY_HOURS;
};

const FloatingChatWidget: React.FC<FloatingChatWidgetProps> = ({
  webhookUrl,
  position = 'bottom-right',
  theme,
  layout,
  behavior
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Load existing session on component mount
  useEffect(() => {
    const existingSessionId = storage.get(STORAGE_KEYS.SESSION_ID);
    const sessionTimestamp = storage.get(STORAGE_KEYS.SESSION_TIMESTAMP);
    
    if (existingSessionId && sessionTimestamp) {
      // Check if session is expired
      if (isSessionExpired(sessionTimestamp)) {
        storage.clear();
      } else {
        setSessionId(existingSessionId);
      }
    }
  }, []);

  const handleOpenChat = () => {
    setIsOpen(true);
    // Only generate new session ID if there is no session
    if (!sessionId) {
      const newSessionId = crypto.randomUUID();
      setSessionId(newSessionId);
      
      // Save session data
      storage.set(STORAGE_KEYS.SESSION_ID, newSessionId);
      storage.set(STORAGE_KEYS.SESSION_TIMESTAMP, new Date().toISOString());
    }
  };

  // Only minimize, do not reset session
  const handleMinimizeChat = () => {
    setIsOpen(false);
    // DO NOT reset session - this preserves user data and chat history
  };

  // End chat: reset session and minimize
  const handleEndChat = () => {
    setIsOpen(false);
    setSessionId(null);
    
    // Clear all session data
    storage.clear();
  };

  const getPositionStyles = () => {
    const baseStyles = {
      position: 'fixed' as const,
      zIndex: 1000,
    };

    switch (position) {
      case 'bottom-right':
        return { ...baseStyles, bottom: 20, right: 20 };
      case 'bottom-left':
        return { ...baseStyles, bottom: 20, left: 20 };
      case 'top-right':
        return { ...baseStyles, top: 20, right: 20 };
      case 'top-left':
        return { ...baseStyles, top: 20, left: 20 };
      default:
        return { ...baseStyles, bottom: 20, right: 20 };
    }
  };

  // Support custom chat icon
  const chatIconUrl = layout?.chatIconUrl;

  return (
    <>
      {/* Floating Chat Icon */}
      <Zoom in={!isOpen}>
        <Box sx={getPositionStyles()}>
          <Tooltip title="Chat with us" placement="left">
            <Fab
              color="primary"
              onClick={handleOpenChat}
              sx={{
                width: 60,
                height: 60,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 6px 25px rgba(0,0,0,0.2)',
                },
                transition: 'all 0.3s ease',
                p: 0
              }}
            >
              {chatIconUrl ? (
                <img
                  src={chatIconUrl}
                  alt="Chat"
                  style={{ width: 32, height: 32, objectFit: 'contain', display: 'block', margin: 'auto' }}
                />
              ) : (
                <ChatIcon sx={{ fontSize: 28, color: theme.primary }} />
              )}
            </Fab>
          </Tooltip>
        </Box>
      </Zoom>

      {/* Chat Window */}
      <Fade in={isOpen}>
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1001,
            width: 400,
            height: 600,
            display: isOpen ? 'block' : 'none'
          }}
        >
          <Box sx={{ position: 'relative', height: '100%' }}>
            {/* Minimize Button (X) */}
            <IconButton
              onClick={handleMinimizeChat}
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                zIndex: 1002,
                bgcolor: '#ff4444',
                color: 'white',
                width: 32,
                height: 32,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                '&:hover': {
                  bgcolor: '#cc0000',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
            {/* End Chat Button */}
            <Button
              onClick={handleEndChat}
              variant="outlined"
              color="error"
              size="small"
              sx={{
                position: 'absolute',
                top: -10,
                right: 30,
                zIndex: 1002,
                bgcolor: 'white',
                color: '#ff4444',
                borderColor: '#ff4444',
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 1.5,
                py: 0.5,
                minWidth: 0,
                '&:hover': {
                  bgcolor: '#ffeaea',
                  borderColor: '#cc0000',
                  color: '#cc0000',
                },
                transition: 'all 0.2s ease'
              }}
            >
              End Chat
            </Button>
            {/* Chat Component */}
            {sessionId && (
              <N8nChat
                webhookUrl={webhookUrl}
                height="600px"
                width="400px"
                theme={theme}
                layout={layout}
                behavior={behavior}
                sessionId={sessionId}
                key={sessionId}
              />
            )}
          </Box>
        </Box>
      </Fade>
    </>
  );
};

export default FloatingChatWidget; 